import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { useAppContext } from "@components/Provider";
import { apiRequestWithRetry, MakeStepAPIPostReq } from "src/services/ClientSideAPIService";
import { shorten } from "helpers/Helper";

import styles from "./InterestPageHeader.module.css";
import "./InterestPageHeader.css";

const Deals = dynamic(() => import("../Deals/Deals"), { ssr: false });

const InterestPageHeader = ({ props, siteID, bisacs, isMobile, title }) => {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false); // State to manage header expansion
  const [pageParamExists, setPageParamExists] = useState(false);
  const { retailerCookie } = useAppContext();

  const searchParams = useSearchParams();
  const pageEndRef = useRef(null);

  const scrollToBottom = () => {
    pageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setPageParamExists(true);
      scrollToBottom();
    } else {
      setPageParamExists(false);
    }
  }, [searchParams])

  useEffect(() => {
    (async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let bisacValues = [];
      if (Array.isArray(bisacs) && bisacs.every(el => typeof el === 'object' && el !== null)) {
        bisacValues = bisacs.map(el => el?.value);
    } else {
        bisacValues = bisacs;
    }
      const reqObj = {
        book: {
          operator: "and",
          filters: [
            {
              field: "partner_title",
              value: false,
              operator: "$eq",
              type: "boolean",
            },
            {
              field: "bisacs",
              value: bisacValues,
              operator: "$in",
              type: "Array",
            },
          ],
          projectionArgs: {
            image: 1,
            title: 1,
            authors: 1,
            "customcategories.Name": 1,
            bisacs: 1,
            primary_isbn: 1,
            price: 1,
            retailers: 1,
          },
        },
        bookdata: {
          operator: "and",
          filters: [],
          projectionArgs: {
            batch: 1,
            "US.dlp": 1,
            "US.dlpCurrencyCode": 1,
            "US.rank.currentRank": 1,
          },
        },
        milestone: {
          operator: "and",
          filters: [],
        },
        campaign: {
          operator: "and",
          filters: [
            {
              field: "daterange-all",
              value: {
                startdate: today,
                enddate: today,
              },
              operator: "$or",
              type: "daterange",
            },
          ],
        },
        authorDetails: {
          operator: "and",
          filters: [],
          projectionArgs: null,
        },
        exclusion: {
          bloodhoundfrontlist: {
            operator: "and",
            filters: [],
          },
          blocklist: {
            operator: "and",
            filters: [],
          },
          excludebookincampaigntypeanddatarange: {
            operator: "and",
            filters: [],
          },
          excludecampaigntype: {
            operator: "and",
            filters: [],
          },
        },
        meta: {
          raninterritory: true,
          excludebooksindaterange: null,
          selectedCountryDropdown: "US",
        },
        postprocessing: {
          campaignType: 1,
          customCategories: [],
          goldboxTheme: [],
          kbdTheme: [],
        },
        sortby: [{ field: "bookdata.US.rank.currentRank", order: "asc" }],
      };
      const response = await MakeStepAPIPostReq(
        "deals",
        reqObj,
        true
      );
      if (response && response.nomination && response.nomination.length > 0) {
        const bookData = {
          props: { affiliate: { props: response?.nomination[0] } },
        };
        const retailerLinks = await apiRequestWithRetry(
          `books/generateretailerlinks/${bookData.props.affiliate.props.primary_isbn}/${siteID}`
        );

        for (let i in retailerLinks) {
          if (
            bookData.props.affiliate.props.retailers.includes(
              retailerLinks[i].name
            ) ||
            bookData.props.affiliate.props.retailers[0] === "ALL"
          ) {
            bookData.props.affiliate.props[retailerLinks[i].ref] =
              retailerLinks[i].url;
          }
        }
        bookData.props.affiliate.props.urlType = "Internal Link";
        bookData.props.salePrice = bookData.props.affiliate.props.promoprice;
        setBook(bookData);
      }
      setLoading(false);
    })();
  }, [bisacs, siteID]);

  const truncatedHeader = shorten(props?.header || "", 1000);
  const fullHeader = props?.header || "";

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const headerContent = isExpanded
    ? fullHeader
    : isMobile
      ? truncatedHeader
      : fullHeader;

  return (
    <>
      <div className={[styles["interest-page-header"], "interest-page-header"].join(" ")}>
        <div className={styles["headerCopy"]}>
          <h1 className={styles["header-title"]} dangerouslySetInnerHTML={{ __html: title }} />
          <div
            dangerouslySetInnerHTML={{ __html: headerContent }}
          />
          {isMobile && (
            <button className={styles["read-more-button"]} onClick={toggleReadMore}>
              {isExpanded ? "Read Less" : "READ MORE"}
            </button>
          )}
        </div>
        <div className={[styles["book-deals"], "book-deals"].join(" ")}>
          <h2 className={styles["title"]}>{props?.currentDealTitle}</h2>
          {loading ? (
            <div className={styles["loader"]}>Loading...</div>
          ) : (
            book && (
              <Deals
                deals={[book]}
                retailerCookie={retailerCookie}
                titleText={""}
                showIndicators={false}
                interestPage={true}
              />
            )
          )}
        </div>
      </div>
      <div
        ref={pageEndRef}
        style={{ height: pageParamExists ? "50px" : "auto" }}
      />
    </>
  );
};

export default InterestPageHeader;
